<script setup lang="ts">
// Tablet+ left-rail nav, paired with BottomNav (mobile). Same items,
// same badge vocabulary, same active-state treatment. AppShell renders
// one or the other based on the breakpoint via CSS — see AppShell.vue.
//
// Lane B will append a 5th "Account" item to the tabs array (icon
// `pi-user`); leave that composition pattern alone here so the swap is
// one-line.
import { computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { useAuctionEvent } from '@/shell/stores/auctionEvent';
import { useMyBidsStore } from '@/features/my-bids';

const route = useRoute();
const router = useRouter();
const auctionEvent = useAuctionEvent();
const myBids = useMyBidsStore();

const slug = computed(() => (route.params.slug as string | undefined) ?? auctionEvent.slug ?? '');

interface Tab {
  key: 'catalog' | 'my-bids' | 'wishlist' | 'wins' | 'account';
  label: string;
  icon: string;
  path: () => string;
  matches: () => boolean;
  badge?: () => 'red' | 'green' | null;
  // Screen-reader text for the badge dot — without it the indicator is
  // color-only and invisible to assistive tech.
  badgeLabel?: string;
}

// Keep this array structurally identical to BottomNav.tabs so the two
// navs stay in sync. Lane B adds the 5th entry to both.
const tabs: Tab[] = [
  {
    key: 'catalog',
    label: 'Catalog',
    icon: 'pi pi-th-large',
    path: () => `/${slug.value}/items`,
    matches: () => route.path.endsWith('/items') || route.path.includes('/items/'),
  },
  {
    key: 'my-bids',
    label: 'My Bids',
    icon: 'pi pi-bookmark',
    path: () => `/${slug.value}/my-bids`,
    matches: () => route.path.endsWith('/my-bids'),
    badge: () => (myBids.losing.length > 0 ? 'red' : null),
    badgeLabel: "You've been outbid",
  },
  {
    key: 'wishlist',
    label: 'Wishlist',
    icon: 'pi pi-heart',
    path: () => `/${slug.value}/wishlist`,
    matches: () => route.path.endsWith('/wishlist'),
  },
  {
    key: 'wins',
    label: 'Wins',
    icon: 'pi pi-star',
    path: () => `/${slug.value}/wins`,
    matches: () => route.path.endsWith('/wins'),
    badge: () =>
      auctionEvent.event?.status === 'closed' && myBids.won.length > 0
        ? 'green'
        : null,
    badgeLabel: 'You have items to review',
  },
  {
    key: 'account',
    label: 'Account',
    icon: 'pi pi-user',
    path: () => `/${slug.value}/account`,
    matches: () => route.path.includes('/account'),
  },
];

function navigate(tab: Tab): void {
  if (!slug.value) return;
  router.push(tab.path());
}
</script>

<template>
  <nav
    class="snav"
    aria-label="Primary"
    data-testid="side-nav"
  >
    <ul class="snav__list">
      <li
        v-for="tab in tabs"
        :key="tab.key"
      >
        <button
          type="button"
          class="snav__tab"
          :class="{ 'snav__tab--active': tab.matches() }"
          :data-testid="`side-nav__${tab.key}`"
          @click="navigate(tab)"
        >
          <span class="snav__icon-wrap">
            <i
              :class="tab.icon"
              aria-hidden="true"
            />
            <span
              v-if="tab.badge?.()"
              class="snav__dot"
              :class="`snav__dot--${tab.badge?.()}`"
              role="img"
              :aria-label="tab.badgeLabel"
              :data-testid="tab.key === 'my-bids' && tab.badge?.() === 'red' ? 'side-nav__outbid-indicator' : undefined"
            />
          </span>
          <span class="snav__label">{{ tab.label }}</span>
        </button>
      </li>
    </ul>
  </nav>
</template>

<style scoped>
.snav {
  width: var(--app-sidenav-width);
  flex-shrink: 0;
  background: transparent;
  border-right: var(--app-border-width-default) solid var(--app-border-muted);
  padding: var(--app-space-16) var(--app-space-12);
  padding-top: calc(var(--app-shell-top-height, 56px) + var(--app-space-16));
  height: 100vh;
  position: sticky;
  top: 0;
  overflow-y: auto;
  box-sizing: border-box;
}
.snav__list {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: var(--app-space-4);
}
.snav__tab {
  all: unset;
  display: flex;
  align-items: center;
  gap: var(--app-space-8);
  width: 100%;
  padding: var(--app-space-8) var(--app-space-12);
  border-radius: var(--app-radius-sm);
  cursor: pointer;
  color: var(--app-text-primary);
  box-sizing: border-box;
  transition: background 120ms ease, color 120ms ease;
}
.snav__tab:hover {
  background: var(--app-background-hover);
}
.snav__tab:focus-visible {
  outline: var(--app-border-width-default) solid var(--app-border-hover);
  outline-offset: 2px;
}
.snav__tab--active,
.snav__tab--active:hover {
  background: var(--app-highlight-background);
  color: var(--app-primary-color);
  font-weight: var(--app-font-weight-medium);
}
.snav__icon-wrap {
  position: relative;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--app-space-16);
  color: var(--app-icon-muted);
  font-size: var(--app-font-size-body-sm);
}
.snav__tab--active .snav__icon-wrap {
  color: var(--app-primary-color);
}
.snav__dot {
  position: absolute;
  top: -2px;
  right: -4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  border: 2px solid var(--app-background-primary);
}
.snav__dot--red {
  background: var(--app-danger);
}
.snav__dot--green {
  background: var(--app-success);
}
.snav__label {
  flex: 1;
  font-size: var(--app-font-size-body-base);
  line-height: var(--app-line-height-body-base);
  font-weight: inherit;
}
</style>
